import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  ProgressBar,
  Row,
  Spinner,
  Stack,
} from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import { faBattery, faSearch } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { emitCameraThresholdEvents } from 'src/services/cameraThresholdEvents';
import {
  APP_EVENTS,
  captureAppEvent,
  secondsSince,
} from 'src/services/posthogEvents';

import useRemoteBoundStore from 'src/store/useRemoteBoundStore';

import ConnectCameraList from 'src/components/custom_components/ConnectCameraList';
import ManualConnectCamera from 'src/components/custom_components/ManualConnectCamera';
import { VolumeControl } from 'src/components/custom_components/VolumeControl';

import { Api } from 'src/models/Api.model';
import { AutoConnectCameraStatus } from 'src/models/Camera.model';
import CameraConnection from 'src/models/Camera/CameraConnection';
import { I18NextNS } from 'src/models/Language.model';

import { captureError } from 'src/utils/errorReporting';
import { convertToByte } from 'src/utils/parseStorage';

interface CameraSettingsProps {
  ipList: string[];
  setIpList: Dispatch<SetStateAction<string[]>>;
}

const CameraSettings = ({ ipList, setIpList }: CameraSettingsProps) => {
  const { setCameraData, cameraData, setAutoConnectCameraStatus } =
    useRemoteBoundStore();

  const [scanning, setScanning] = useState(false);

  const [hasNotFoundCameras, setHasNotFoundCameras] = useState(false);
  const [hasCameraServerError, setHasCameraServerError] = useState(false);

  const {
    t,
    i18n: { language },
  } = useTranslation(I18NextNS.PAGES);

  const { ip: connectedCameraIp } = cameraData || {};

  const [isReloadingCameraData, setIsReloadingCameraData] = useState(false);

  const reloadCameraData = useCallback(async () => {
    try {
      setIsReloadingCameraData(true);

      const errorMessage = t('message.error.cameraIpMissingMsg', {
        ns: I18NextNS.COMMON,
      });
      if (!connectedCameraIp) {
        throw new Error(errorMessage);
      }
      const res = await CameraConnection.loadCameraData(
        connectedCameraIp,
        errorMessage,
      );

      setCameraData(res);
      emitCameraThresholdEvents(res);
    } catch (error) {
      captureError(error, 'Failed to reload camera data');

      Api.alertBox({
        title: t('bootbox.title.Error', { ns: I18NextNS.COMMON }),
        message: t('message.error.failedToReloadCameraData', {
          ns: I18NextNS.COMMON,
        }),
        locale: language,
        type: 'error',
      });
    } finally {
      setIsReloadingCameraData(false);
    }
  }, [connectedCameraIp, language, setCameraData, t]);

  useEffect(() => {
    if (connectedCameraIp) {
      reloadCameraData();
    }
  }, [connectedCameraIp, reloadCameraData]);

  const disconnectCamera = () => {
    Api.confirmBox({
      title: t('bootbox.title.Confirm', { ns: I18NextNS.COMMON }),
      message: t('settings.cameraModes.cameraConnection.disconnectMessage'),
      locale: language,
      callback: (yes) => {
        if (yes !== true) {
          return;
        }

        const serial = cameraData?.deviceSN;
        setCameraData(undefined);
        setAutoConnectCameraStatus(AutoConnectCameraStatus.FAILED);
        captureAppEvent(APP_EVENTS.CAMERA_MANUALLY_DISCONNECTED, {
          camera_serial_number: serial,
        });
      },
    });
  };

  const clearStorage = () => {
    Api.confirmBox({
      title: t('bootbox.title.Confirm', { ns: I18NextNS.COMMON }),
      message: t('settings.cameraModes.cameraConnection.clearStorageMessage'),
      locale: language,
      callback: async (yes) => {
        if (yes !== true) {
          return;
        }

        await CameraConnection.getInstance().formatStorage();

        reloadCameraData();
      },
    });
  };

  const powerOffCamera = () => {
    Api.confirmBox({
      title: t('bootbox.title.Confirm', { ns: I18NextNS.COMMON }),
      message: t('settings.cameraModes.cameraConnection.powerOffMessage'),
      locale: language,
      callback: async (yes) => {
        if (yes !== true) {
          return;
        }

        setCameraData(undefined);
        setIpList([]);

        // Whenever we call this endpoint, the camera will throw an error.
        CameraConnection.getInstance().powerOff();
      },
    });
  };

  const [scanPercentage, setScanPercentage] = useState(0);

  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const [numberOfLocalIPs, setNumberOfLocalIPs] = useState(0);

  const scanCameras = async () => {
    captureAppEvent(APP_EVENTS.CAMERA_AUTO_SCAN_INITIATED);
    const scanStart = performance.now();
    try {
      const localIPList = await Api.getLocalIpAddress();
      const numberOfLocalIPs = localIPList.length;
      setNumberOfLocalIPs(numberOfLocalIPs);

      setScanPercentage(0);
      setIpList([]);
      setHasNotFoundCameras(false);
      setCameraData(undefined);

      setScanning(true);

      let count = 0;

      timerRef.current = setInterval(() => {
        count++;
        if (count <= 256 * numberOfLocalIPs) {
          setScanPercentage(count * numberOfLocalIPs * 1000);
        } else {
          setScanPercentage(256 * numberOfLocalIPs * 1000);
        }
      }, 1000);

      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }

      const controller = new AbortController();
      abortControllerRef.current = controller;

      const IpList = await CameraConnection.getCameraIpList(
        localIPList,
        controller.signal,
      );

      setScanPercentage(256 * numberOfLocalIPs * 1000);
      clearInterval(timerRef.current);
      timerRef.current = null;

      captureAppEvent(APP_EVENTS.CAMERA_SCAN_COMPLETED, {
        count: IpList.length,
        duration_seconds: secondsSince(scanStart),
      });

      if (IpList.length === 0) {
        setHasNotFoundCameras(true);
        return Api.alertBox({
          title: t('bootbox.title.Error', { ns: I18NextNS.COMMON }),
          message: t('settings.cameraModes.cameraConnection.cameraNotFound'),
          callback: () => {
            setScanning(false);
          },
          locale: language,
          type: 'error',
        });
      }

      setIpList(IpList);
      setHasCameraServerError(false);
      setScanning(false);
    } catch (err) {
      captureError(err, 'Failed to scan camera');

      Api.alertBox({
        title: t('bootbox.title.Error', { ns: I18NextNS.COMMON }),
        message: t('message.error.failedToScanCamera', {
          ns: I18NextNS.COMMON,
        }),
        locale: language,
        type: 'error',
      });
      setScanning(false);
      setHasCameraServerError(true);
    }
  };

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }

      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, []);

  return (
    <div className="d-flex flex-column pt-3">
      <Card body className="mt-2 shadow">
        {cameraData ? (
          <>
            <div
              className="text-gray-400 fw-semibold mb-3"
              data-test="settings-connected-camera-section"
            >
              {t(
                'settings.cameraModes.cameraConnection.connectedToOptainFundusCamera',
              )}
            </div>
            {isReloadingCameraData ? (
              <div className="d-flex justify-content-center my-4">
                <Spinner
                  variant="primary"
                  data-test="settings-camera-reload-spinner"
                />
              </div>
            ) : (
              <>
                <Row className="d-flex justify-content-between">
                  <Col>
                    <div className="mb-2">
                      <h6 data-test="settings-camera-serial-number-label">
                        {t(
                          'settings.cameraModes.cameraConnection.serialNumber',
                        )}
                      </h6>
                      <div
                        className="bg-gray-100 rounded py-2 px-3 w-75"
                        data-test="settings-camera-serial-number"
                      >
                        {cameraData?.deviceSN || '-'}
                      </div>
                    </div>
                    <div className="mb-3">
                      <h6 data-test="settings-camera-version-label">
                        {t('settings.cameraModes.cameraConnection.version')}
                      </h6>
                      <div
                        className="bg-gray-100 rounded py-2 px-3 w-75"
                        data-test="settings-camera-version"
                      >
                        {cameraData?.versionInfo.version || '-'}
                      </div>
                    </div>
                    <div
                      className="mb-2 text-center bg-gray-100 rounded p-2 d-inline-block text-black"
                      data-test="settings-camera-battery"
                    >
                      <FontAwesomeIcon
                        icon={faBattery}
                        className="ms-1 me-2"
                      ></FontAwesomeIcon>
                      {cameraData && cameraData.batteryInfo.battCap
                        ? `${t('settings.cameraModes.cameraConnection.battery')}: ${cameraData.batteryInfo.battCap}%`
                        : '-'}
                    </div>

                    <VolumeControl />
                  </Col>

                  <Col className="d-flex flex-column">
                    <div
                      className="mb-1"
                      data-test="settings-camera-storage-label"
                    >
                      {t('settings.cameraModes.cameraConnection.storage')}
                    </div>
                    <ProgressBar
                      now={convertToByte(
                        cameraData?.storage.usage.split('/')[0],
                      )}
                      max={convertToByte(
                        cameraData?.storage.usage.split('/')[1],
                      )}
                      variant="primary"
                      style={{ height: 42 }}
                      className="w-75"
                      data-test="settings-camera-storage"
                    />
                    <div className="d-flex align-items-center gap-2 mt-1 w-75">
                      <span
                        className="text-gray-400"
                        data-test="settings-camera-storage-usage"
                      >
                        {cameraData ? cameraData.storage.usage : '-'}
                      </span>
                      <Button
                        variant="link"
                        className="text-primary-500 p-0 ms-auto fw-semibold"
                        onClick={clearStorage}
                        data-test="settings-clear-camera-storage-button"
                        data-track="settings.camera.clear-storage"
                        data-observe
                      >
                        {t(
                          'settings.cameraModes.cameraConnection.clearCameraStorage',
                        )}
                      </Button>
                    </div>
                  </Col>
                </Row>
                <div className="d-flex justify-content-between align-items-center mt-4 pt-3 fw-semibold">
                  {cameraData && (
                    <Button
                      variant="danger"
                      className="shadow-lg"
                      onClick={disconnectCamera}
                      data-test="settings-disconnect-camera-button"
                      data-track="settings.camera.disconnect"
                      data-observe
                    >
                      {t('settings.cameraModes.cameraConnection.disconnect')}
                    </Button>
                  )}
                  <div className="d-flex gap-2">
                    <Button
                      variant="primary-200"
                      className="text-primary-500 fw-semibold"
                      onClick={reloadCameraData}
                      data-test="settings-refresh-camera-button"
                      data-track="settings.camera.refresh"
                      data-observe
                    >
                      {t('settings.cameraModes.cameraConnection.refresh')}
                    </Button>
                    <Button
                      variant="primary"
                      className="ms-2 shadow-lg d-flex flex-column align-items-center justify-content-center fw-semibold"
                      onClick={powerOffCamera}
                      data-test="settings-power-off-camera-button"
                      data-track="settings.camera.power-off"
                      data-observe
                    >
                      {t('settings.cameraModes.cameraConnection.power')}
                    </Button>
                  </div>
                </div>
              </>
            )}
          </>
        ) : (
          <div className="d-flex flex-column gap-1">
            <h5 data-test="settings-available-cameras-section-title">
              {t('settings.cameraModes.cameraConnection.title')}
            </h5>

            {(!hasNotFoundCameras || scanning) && (
              <p>{t('settings.cameraModes.cameraConnection.desc')}</p>
            )}

            {hasCameraServerError && !scanning && (
              <div className="d-flex">
                <Alert variant="danger" className="px-4 fw-semibold">
                  {t('settings.cameraModes.cameraConnection.cameraServerError')}
                </Alert>
              </div>
            )}

            {hasNotFoundCameras && !scanning && (
              <>
                <div className="text-gray-400 fw-semibold">
                  {t('settings.cameraModes.cameraConnection.noCameraFound')}
                </div>
                <div className="text-gray-400 mb-2">
                  {t('settings.cameraModes.cameraConnection.noCameraFoundDesc')}
                </div>
              </>
            )}

            <ConnectCameraList ipList={ipList} />

            {scanning ? (
              <Alert className="text-center w-50">
                <ProgressBar
                  now={scanPercentage}
                  max={256 * numberOfLocalIPs * 1000}
                  variant="primary"
                  className="mb-3"
                />
                {t('settings.cameraModes.cameraConnection.scanning')}
                ...
              </Alert>
            ) : (
              <Stack direction="horizontal" gap={5}>
                <Button
                  onClick={scanCameras}
                  variant="primary"
                  data-test="settings-automatic-scan-button"
                  data-track="settings.camera.automatic-scan"
                  data-observe
                >
                  <FontAwesomeIcon icon={faSearch} className="me-2" />
                  {t('settings.cameraModes.cameraConnection.automaticScan')}
                </Button>
                <ManualConnectCamera variant="primary" isScanning={scanning} />
              </Stack>
            )}
          </div>
        )}
      </Card>
    </div>
  );
};

export default CameraSettings;
